import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
  Button, Stack, Table, TableHead, TableBody, TableRow, TableCell, Chip, Typography,
} from '@mui/material'
import * as XLSX from 'xlsx'

import { defaultFunction, getFullName } from '../../utilities/utilities'

import './PendingVisitsModal.sass'

/**
 * Blocking modal shown right after login (from `LoadPage`) when
 * `quarantined_visits` is not empty — i.e. the previous session ended
 * (logout) with visits that had never been confirmed by the server.
 *
 * The user must explicitly choose to resync or discard them; there is no
 * backdrop/escape dismissal since this represents real check-in data that
 * would otherwise silently disappear.
 */
function PendingVisitsModal(props) {
  const {
    open = false,
    visits = [],
    onSync = defaultFunction,
    onDiscard = defaultFunction,
  } = props

  const { t } = useTranslation()
  const [processing, setProcessing] = useState(false)

  const handleSync = async () => {
    setProcessing(true)
    try {
      await onSync()
    } finally {
      setProcessing(false)
    }
  }

  const handleDiscard = async () => {
    setProcessing(true)
    try {
      await onDiscard()
    } finally {
      setProcessing(false)
    }
  }

  const handleDownload = () => {
    // Exporta el registro completo de la visita pendiente (mismos campos que
    // el modal "Editar el registro de visitante" — EditDialog.js —, excepto
    // la foto, que no aplica sin conexión), no solo el resumen visible en la
    // tabla de este modal.
    const headers = [
      t('Fullname'),
      t('Identification'),
      t('Go to'),
      t('Comes from'),
      t('pending_visits_modal_col_entry_time'),
      t('Exit time'),
      t('Card'),
      t('Laptop'),
      t('license_plate'),
      t('Notes'),
      t('sync_status'),
      t('pending_visits_modal_col_sync_error'),
    ]
    const body = visits.map(visit => [
      [visit.first_name, visit.last_name_one, visit.last_name_two].filter(Boolean).join(' '),
      visit.identification || '',
      visit.destination?.name || '',
      visit.comes_from || '',
      visit.entry_time ? new Date(visit.entry_time).toLocaleString() : '',
      visit.exit_time ? new Date(visit.exit_time).toLocaleString() : '',
      visit.card_number || '',
      visit.has_laptop ? (visit.laptop_serie || t('Yes')) : t('No'),
      visit.license_plate || '',
      visit.notes || '',
      visit.syncStatus === 'failed' ? t('sync_failed') : t('sync_pending'),
      visit.syncError || '',
    ])

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...body])
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'pending_visits')

    const date = new Date().toISOString().slice(0, 10)
    XLSX.writeFile(workbook, `pending_visits_${date}.xlsx`)
  }

  return (
    <Dialog
      className="pending-visits-modal"
      open={open}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown
    >
      <DialogTitle>{t('pending_visits_modal_title')}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t('pending_visits_modal_description', { count: visits.length })}
        </DialogContentText>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('Fullname')}</TableCell>
              <TableCell>{t('Identification')}</TableCell>
              <TableCell>{t('pending_visits_modal_col_entry_time')}</TableCell>
              <TableCell>{t('sync_status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visits.map(visit => (
              <TableRow key={visit.uuid}>
                <TableCell>{getFullName(visit)}</TableCell>
                <TableCell>{visit.identification}</TableCell>
                <TableCell>
                  {visit.entry_time ? new Date(visit.entry_time).toLocaleString() : '—'}
                </TableCell>
                <TableCell>
                  <Stack spacing={0.5}>
                    <Chip
                      size="small"
                      label={visit.syncStatus === 'failed' ? t('sync_failed') : t('sync_pending')}
                      color={visit.syncStatus === 'failed' ? 'error' : 'warning'}
                      variant="outlined"
                    />
                    {visit.syncStatus === 'failed' && visit.syncError && (
                      <Typography variant="caption" color="error">
                        {visit.syncError}
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <DialogContentText className="pending-visits-modal-discard-warning">
          {t('pending_visits_modal_discard_warning')}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={handleDownload}
          disabled={processing || visits.length === 0}
          sx={{ mr: 'auto' }}
        >
          {t('Download')}
        </Button>
        <Button
          variant="outlined"
          color="error"
          onClick={handleDiscard}
          disabled={processing}
        >
          {t('pending_visits_modal_discard_button')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSync}
          disabled={processing}
        >
          {t('pending_visits_modal_sync_button')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PendingVisitsModal
